import { useProctoredAttempt } from '../components/focus-guard/FocusGuardContext';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import VoiceProfileAssessmentPopup from '../components/assessment/VoiceProfileAssessmentPopup';
import { 
    ClipboardCheck, 
    Play, 
    Clock, 
    CheckCircle, 
    XCircle, 
    Plus,
    Trophy,
    Target,
    FileText,
    ChevronRight,
    Mic
} from 'lucide-react';

const Assessments = () => {
    const { api, isAdmin, isTrainer } = useAuth();
    const [assessments, setAssessments] = useState([]);
    const [topics, setTopics] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    
    // Voice Profile Assessment state
    const [showVoiceAssessment, setShowVoiceAssessment] = useState(false);
    const [voiceAssessmentStatus, setVoiceAssessmentStatus] = useState(null);
    const [pendingAssessmentId, setPendingAssessmentId] = useState(null);
    
    // Assessment taking state
    const [activeAttempt, setActiveAttempt] = useState(null);
    const [currentAssessment, setCurrentAssessment] = useState(null);
    const [answers, setAnswers] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [startTime, setStartTime] = useState(null);
    const [result, setResult] = useState(null);
    useProctoredAttempt({ active: Boolean(activeAttempt && currentAssessment) && !result, label: 'assessment', onForceEnd: () => submitAssessment() });
    
    // Create assessment form
    const [newAssessment, setNewAssessment] = useState({
        topic_id: '',
        title: '',
        description: '',
        time_limit_minutes: 30,
        passing_score: 70,
        questions: []
    });
    const [newQuestion, setNewQuestion] = useState({
        type: 'mcq',
        question: '',
        options: ['', '', '', ''],
        correct_answer: '',
        expected_points: [],
        points: 10
    });

    useEffect(() => {
        fetchData();
        checkVoiceAssessmentStatus();
    }, []);

    const fetchData = async () => {
        try {
            const [assessmentsRes, topicsRes, historyRes] = await Promise.all([
                api.get('/assessments'),
                api.get('/topics'),
                api.get('/assessments/attempts/history')
            ]);
            setAssessments(assessmentsRes.data);
            setTopics(topicsRes.data);
            setHistory(historyRes.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setLoading(false);
        }
    };

    const checkVoiceAssessmentStatus = async () => {
        try {
            const response = await api.get('/voice-profile-assessment/status');
            setVoiceAssessmentStatus(response.data);
        } catch (error) {
            console.error('Failed to check voice assessment status:', error);
        }
    };

    const handleStartAssessment = async (assessmentId) => {
        // Check if user needs to complete voice assessment first
        if (voiceAssessmentStatus?.needs_assessment && !isAdmin && !isTrainer) {
            setPendingAssessmentId(assessmentId);
            setShowVoiceAssessment(true);
            toast.info('Please complete a quick voice assessment first');
            return;
        }
        
        // Proceed with normal assessment
        startAssessment(assessmentId);
    };

    const handleVoiceAssessmentComplete = () => {
        setShowVoiceAssessment(false);
        setVoiceAssessmentStatus({ ...voiceAssessmentStatus, needs_assessment: false });
        
        // Start the pending assessment if there was one
        if (pendingAssessmentId) {
            startAssessment(pendingAssessmentId);
            setPendingAssessmentId(null);
        }
    };

    const handleVoiceAssessmentDismiss = () => {
        setShowVoiceAssessment(false);
        setPendingAssessmentId(null);
    };

    const startAssessment = async (assessmentId) => {
        try {
            const response = await api.post(`/assessments/${assessmentId}/start`);
            setActiveAttempt(response.data.attempt_id);
            setCurrentAssessment(response.data.assessment);
            setAnswers(new Array(response.data.assessment.questions.length).fill({ answer: '' }));
            setCurrentQuestionIndex(0);
            setStartTime(Date.now());
            setResult(null);
        } catch (error) {
            toast.error('Failed to start assessment');
        }
    };

    const submitAssessment = async () => {
        if (!activeAttempt || !currentAssessment) return;
        
        const timeTaken = Math.floor((Date.now() - startTime) / 1000);
        
        // Ensure all answers are present (fill empty ones)
        const totalQuestions = currentAssessment.questions?.length || 0;
        const filledAnswers = [];
        for (let i = 0; i < totalQuestions; i++) {
            filledAnswers.push(answers[i] || { answer: '' });
        }
        
        try {
            const response = await api.post(`/assessments/attempt/${activeAttempt}/submit`, {
                assessment_id: currentAssessment.id,
                answers: filledAnswers,
                time_taken_seconds: timeTaken
            });
            setResult(response.data);
            toast.success(response.data.passed ? 'Congratulations! You passed!' : 'Assessment completed');
            fetchData();
        } catch (error) {
            console.error('Assessment submission error:', error.response?.data || error);
            toast.error(error.response?.data?.detail || 'Failed to submit assessment');
        }
    };

    const resetAssessment = () => {
        setActiveAttempt(null);
        setCurrentAssessment(null);
        setAnswers([]);
        setCurrentQuestionIndex(0);
        setStartTime(null);
        setResult(null);
    };

    const handleAnswerChange = (value) => {
        const newAnswers = [...answers];
        newAnswers[currentQuestionIndex] = { answer: value };
        setAnswers(newAnswers);
    };

    const addQuestion = () => {
        if (!newQuestion.question) {
            toast.error('Please enter a question');
            return;
        }
        
        const question = { ...newQuestion };
        if (question.type === 'mcq') {
            question.options = question.options.filter(o => o.trim());
        }
        
        setNewAssessment(prev => ({
            ...prev,
            questions: [...prev.questions, question]
        }));
        
        setNewQuestion({
            type: 'mcq',
            question: '',
            options: ['', '', '', ''],
            correct_answer: '',
            expected_points: [],
            points: 10
        });
        
        toast.success('Question added');
    };

    const createAssessment = async () => {
        if (!newAssessment.topic_id || !newAssessment.title || newAssessment.questions.length === 0) {
            toast.error('Please fill all required fields and add at least one question');
            return;
        }
        
        try {
            await api.post('/assessments', newAssessment);
            toast.success('Assessment created!');
            setCreateDialogOpen(false);
            setNewAssessment({
                topic_id: '',
                title: '',
                description: '',
                time_limit_minutes: 30,
                passing_score: 70,
                questions: []
            });
            fetchData();
        } catch (error) {
            toast.error('Failed to create assessment');
        }
    };

    if (loading) {
        return (
            <MainLayout>
                <div className="flex items-center justify-center h-[60vh]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                </div>
            </MainLayout>
        );
    }

    // Show assessment result
    if (result) {
        return (
            <MainLayout>
                <div className="max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="assessment-result">
                    <Card className={result.passed ? 'border-emerald-500/50' : 'border-yellow-500/50'}>
                        <CardContent className="pt-6 text-center">
                            {result.passed ? (
                                <Trophy className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
                            ) : (
                                <Target className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
                            )}
                            <h2 className="text-2xl font-bold font-heading mb-2">
                                {result.passed ? 'Congratulations!' : 'Keep Practicing!'}
                            </h2>
                            <p className="text-muted-foreground mb-6">
                                {result.message}
                            </p>
                            
                            <div className="flex justify-center gap-8 mb-6">
                                <div className="text-center">
                                    <p className="text-4xl font-bold text-gradient">{result.score}%</p>
                                    <p className="text-sm text-muted-foreground">Your Score</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-4xl font-bold">{result.passing_score}%</p>
                                    <p className="text-sm text-muted-foreground">Passing Score</p>
                                </div>
                            </div>
                            
                            <div className="space-y-3 text-left mb-6">
                                <h3 className="font-semibold">Question Results:</h3>
                                {result.detailed_results?.map((r, i) => (
                                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                                        <span className="text-sm">Question {i + 1}</span>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium">{r.score}/{r.max_score}</span>
                                            {r.correct !== undefined && (
                                                r.correct ? 
                                                    <CheckCircle className="h-4 w-4 text-emerald-500" /> :
                                                    <XCircle className="h-4 w-4 text-red-500" />
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            <Button onClick={resetAssessment} className="btn-primary">
                                Back to Assessments
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </MainLayout>
        );
    }

    // Show active assessment
    if (activeAttempt && currentAssessment) {
        const question = currentAssessment.questions[currentQuestionIndex];
        const totalQuestions = currentAssessment.questions.length;
        const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;
        
        return (
            <MainLayout>
                <div className="max-w-3xl mx-auto space-y-6 animate-fade-in" data-testid="active-assessment">
                    {/* Progress Header */}
                    <Card>
                        <CardContent className="py-4">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium">
                                    Question {currentQuestionIndex + 1} of {totalQuestions}
                                </span>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Clock className="h-4 w-4" />
                                    {currentAssessment.time_limit_minutes} min limit
                                </div>
                            </div>
                            <Progress value={progress} className="h-2" />
                        </CardContent>
                    </Card>
                    
                    {/* Question */}
                    <Card>
                        <CardHeader>
                            <Badge className="w-fit mb-2">{question.type?.toUpperCase()}</Badge>
                            {/* Show scenario if present */}
                            {question.scenario && (
                                <div className="mb-4 p-4 rounded-lg bg-muted/50 border border-muted-foreground/20">
                                    <div className="flex items-start gap-2 mb-2">
                                        <FileText className="h-4 w-4 text-primary mt-0.5" />
                                        <span className="text-sm font-medium text-primary">Scenario</span>
                                    </div>
                                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                                        {question.scenario}
                                    </p>
                                </div>
                            )}
                            <CardTitle className="text-xl">{question.question}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {question.type === 'mcq' && question.options ? (
                                <RadioGroup
                                    value={answers[currentQuestionIndex]?.answer || ''}
                                    onValueChange={handleAnswerChange}
                                >
                                    {question.options.map((option, i) => (
                                        <div key={i} className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                                            <RadioGroupItem value={option} id={`option-${i}`} />
                                            <Label htmlFor={`option-${i}`} className="flex-1 cursor-pointer">
                                                {option}
                                            </Label>
                                        </div>
                                    ))}
                                </RadioGroup>
                            ) : (
                                <Textarea
                                    value={answers[currentQuestionIndex]?.answer || ''}
                                    onChange={(e) => handleAnswerChange(e.target.value)}
                                    placeholder="Type your answer here..."
                                    rows={6}
                                    className="resize-none"
                                />
                            )}
                            
                            <div className="flex justify-between pt-4">
                                <Button
                                    variant="outline"
                                    onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
                                    disabled={currentQuestionIndex === 0}
                                >
                                    Previous
                                </Button>
                                
                                {currentQuestionIndex < totalQuestions - 1 ? (
                                    <Button
                                        onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
                                    >
                                        Next
                                        <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={submitAssessment}
                                        className="btn-primary"
                                    >
                                        Submit Assessment
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </MainLayout>
        );
    }

    // Show assessment list
    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in" data-testid="assessments-page">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
                            <ClipboardCheck className="h-8 w-8 text-primary" />
                            Assessments
                        </h1>
                        <p className="text-muted-foreground mt-1">Test your knowledge and skills</p>
                    </div>
                    
                    {(isAdmin || isTrainer) && (
                        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                            <DialogTrigger asChild>
                                <Button className="btn-primary" data-testid="create-assessment-btn">
                                    <Plus className="h-4 w-4 mr-2" />
                                    Create Assessment
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                                <DialogHeader>
                                    <DialogTitle>Create New Assessment</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Topic</Label>
                                            <Select
                                                value={newAssessment.topic_id}
                                                onValueChange={(v) => setNewAssessment(prev => ({ ...prev, topic_id: v }))}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select topic" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {topics.map(t => (
                                                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Title</Label>
                                            <Input
                                                value={newAssessment.title}
                                                onChange={(e) => setNewAssessment(prev => ({ ...prev, title: e.target.value }))}
                                                placeholder="Assessment title"
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <Label>Description</Label>
                                        <Textarea
                                            value={newAssessment.description}
                                            onChange={(e) => setNewAssessment(prev => ({ ...prev, description: e.target.value }))}
                                            placeholder="Assessment description"
                                        />
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Time Limit (minutes)</Label>
                                            <Input
                                                type="number"
                                                value={newAssessment.time_limit_minutes}
                                                onChange={(e) => setNewAssessment(prev => ({ ...prev, time_limit_minutes: parseInt(e.target.value) }))}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Passing Score (%)</Label>
                                            <Input
                                                type="number"
                                                value={newAssessment.passing_score}
                                                onChange={(e) => setNewAssessment(prev => ({ ...prev, passing_score: parseInt(e.target.value) }))}
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Questions List */}
                                    <div className="space-y-2">
                                        <Label>Questions ({newAssessment.questions.length})</Label>
                                        <div className="space-y-2 max-h-32 overflow-y-auto">
                                            {newAssessment.questions.map((q, i) => (
                                                <div key={i} className="flex items-center justify-between p-2 rounded bg-muted/50 text-sm">
                                                    <span>{i + 1}. {q.question.substring(0, 50)}...</span>
                                                    <Badge>{q.type}</Badge>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    {/* Add Question */}
                                    <Card className="border-dashed">
                                        <CardContent className="pt-4 space-y-3">
                                            <div className="flex gap-4">
                                                <Select
                                                    value={newQuestion.type}
                                                    onValueChange={(v) => setNewQuestion(prev => ({ ...prev, type: v }))}
                                                >
                                                    <SelectTrigger className="w-32">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="mcq">MCQ</SelectItem>
                                                        <SelectItem value="scenario">Scenario</SelectItem>
                                                        <SelectItem value="text_answer">Text</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                <Input
                                                    value={newQuestion.question}
                                                    onChange={(e) => setNewQuestion(prev => ({ ...prev, question: e.target.value }))}
                                                    placeholder="Question text"
                                                    className="flex-1"
                                                />
                                            </div>
                                            
                                            {newQuestion.type === 'mcq' && (
                                                <div className="grid grid-cols-2 gap-2">
                                                    {newQuestion.options.map((opt, i) => (
                                                        <Input
                                                            key={i}
                                                            value={opt}
                                                            onChange={(e) => {
                                                                const newOpts = [...newQuestion.options];
                                                                newOpts[i] = e.target.value;
                                                                setNewQuestion(prev => ({ ...prev, options: newOpts }));
                                                            }}
                                                            placeholder={`Option ${i + 1}`}
                                                        />
                                                    ))}
                                                    <Input
                                                        value={newQuestion.correct_answer}
                                                        onChange={(e) => setNewQuestion(prev => ({ ...prev, correct_answer: e.target.value }))}
                                                        placeholder="Correct answer (exact text)"
                                                        className="col-span-2"
                                                    />
                                                </div>
                                            )}
                                            
                                            <Button type="button" variant="outline" size="sm" onClick={addQuestion}>
                                                <Plus className="h-4 w-4 mr-1" /> Add Question
                                            </Button>
                                        </CardContent>
                                    </Card>
                                    
                                    <Button onClick={createAssessment} className="w-full">
                                        Create Assessment
                                    </Button>
                                </div>
                            </DialogContent>
                        </Dialog>
                    )}
                </div>

                {/* Available Assessments */}
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {assessments.map((assessment) => {
                        const topic = topics.find(t => t.id === assessment.topic_id);
                        return (
                            <Card key={assessment.id} className="card-hover" data-testid={`assessment-${assessment.id}`}>
                                <CardHeader>
                                    <div className="flex items-start justify-between">
                                        <div className="p-2 rounded-lg bg-primary/10">
                                            <FileText className="h-5 w-5 text-primary" />
                                        </div>
                                        <Badge variant="outline">{assessment.questions?.length || 0} questions</Badge>
                                    </div>
                                    <CardTitle className="text-lg mt-3">{assessment.title}</CardTitle>
                                    <CardDescription>{assessment.description}</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                                        <span className="flex items-center gap-1">
                                            <Clock className="h-4 w-4" />
                                            {assessment.time_limit_minutes} min
                                        </span>
                                        <span>Pass: {assessment.passing_score}%</span>
                                    </div>
                                    {topic && (
                                        <Badge variant="secondary" className="mb-3">{topic.name}</Badge>
                                    )}
                                    <Button 
                                        onClick={() => handleStartAssessment(assessment.id)} 
                                        className="w-full"
                                        data-testid={`start-assessment-${assessment.id}`}
                                    >
                                        <Play className="h-4 w-4 mr-2" />
                                        Start Assessment
                                    </Button>
                                </CardContent>
                            </Card>
                        );
                    })}
                    
                    {assessments.length === 0 && (
                        <Card className="col-span-full">
                            <CardContent className="py-12 text-center text-muted-foreground">
                                <ClipboardCheck className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                <p>No assessments available yet.</p>
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* Assessment History */}
                {history.length > 0 && (
                    <Card>
                        <CardHeader>
                            <CardTitle>Your Assessment History</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {history.map((attempt) => (
                                    <div 
                                        key={attempt.id} 
                                        className="flex items-center justify-between p-4 rounded-lg bg-muted/50"
                                    >
                                        <div>
                                            <p className="font-medium">{attempt.assessment_title || 'Assessment'}</p>
                                            <p className="text-sm text-muted-foreground">
                                                {new Date(attempt.started_at).toLocaleDateString()}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <span className="text-lg font-bold">
                                                {attempt.score !== null ? `${attempt.score}%` : '-'}
                                            </span>
                                            {attempt.passed !== undefined && (
                                                attempt.passed ? 
                                                    <CheckCircle className="h-5 w-5 text-emerald-500" /> :
                                                    <XCircle className="h-5 w-5 text-yellow-500" />
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
            
            {/* Voice Profile Assessment - Show when user needs to complete it before assessment */}
            {showVoiceAssessment && (
                <VoiceProfileAssessmentPopup 
                    forceShow={true}
                    onComplete={handleVoiceAssessmentComplete}
                    onDismiss={handleVoiceAssessmentDismiss}
                />
            )}
        </MainLayout>
    );
};

export default Assessments;
